import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { 
  ShoppingBag, 
  Package, 
  DollarSign, 
  TrendingUp,
  TrendingDown,
  Heart,
  MessageCircle,
  ArrowRight,
  Plus,
  Eye,
  Loader2,
  ShoppingCart,
  Wallet,
  Sparkles,
  Info
} from 'lucide-react';
import { Button } from '../components/ui/button';

export default function DashboardPage() {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentWants, setRecentWants] = useState([]);
  const [recentInventory, setRecentInventory] = useState([]);

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [statsRes, wantsRes, inventoryRes] = await Promise.all([
        api.get('/users/stats'),
        api.get('/wtb/my?limit=3'),
        api.get('/inventory/my?limit=3'),
      ]);
      setStats(statsRes.data);
      setRecentWants(wantsRes.data.slice(0, 3));
      setRecentInventory(inventoryRes.data.slice(0, 3));
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 py-12 text-center">
          <h1 className="text-2xl font-bold text-[#1E3A5F] mb-4">Please log in to view your dashboard</h1>
          <Button onClick={() => navigate('/login')} className="bg-[#1E3A5F]">
            Log In
          </Button>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 py-12 flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#1E3A5F]" />
        </div>
      </Layout>
    );
  }

  const statCards = [
    {
      title: 'Active Wants',
      value: stats?.active_wants || 0,
      icon: ShoppingBag,
      color: 'bg-[#C9956C]',
      link: '/my-wants',
      description: 'Your Wants'
    },
    {
      title: 'My Items',
      value: stats?.inventory_items || 0,
      icon: Package,
      color: 'bg-[#1E3A5F]',
      link: '/my-items',
      description: 'Items you own'
    },
    {
      title: 'Items Value',
      value: `$${(stats?.total_inventory_value || 0).toLocaleString()}`,
      icon: Wallet,
      color: 'bg-[#059669]',
      link: '/my-items',
      description: 'Total estimated value'
    },
    {
      title: 'Match Opportunities',
      value: stats?.match_opportunities || 0,
      icon: Heart,
      color: 'bg-[#DC2626]',
      link: '/matches',
      description: 'Potential deals'
    },
  ];

  // Purchase-based savings (what they've actually saved by buying on WANTD)
  const totalSavings = stats?.total_savings || 0;
  const itemsBought = stats?.items_bought || 0;
  const totalSpent = stats?.total_spent || 0;
  const hasPurchases = itemsBought > 0;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Welcome Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-black text-[var(--theme-text-primary)] mb-2">
            Welcome back, {user.username}!
          </h1>
          <p className="text-[var(--theme-text-secondary)]">
            Here's an overview of your marketplace activity
          </p>
        </div>

        {/* WANTD Savings Banner - Based on Purchase History */}
        {hasPurchases && totalSavings > 0 && (
          <Link to="/purchases" className="block mb-8 group">
            <div className="bg-gradient-to-r from-[#059669] to-[#047857] rounded-2xl p-6 text-white relative overflow-hidden group-hover:shadow-xl transition-shadow">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="relative">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5" />
                    <span className="text-white/80 text-sm font-medium">Your WANTD Savings</span>
                  </div>
                  <ArrowRight className="w-5 h-5 text-white/60 group-hover:translate-x-1 transition-transform" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <div className="text-4xl font-black mb-1">
                      ${totalSavings.toLocaleString()}
                    </div>
                    <div className="text-white/70 text-sm">
                      Saved vs buying new
                    </div>
                  </div>
                  <div>
                    <div className="text-4xl font-black mb-1">
                      ${totalSpent.toLocaleString()}
                    </div>
                    <div className="text-white/70 text-sm">
                      Total spent
                    </div>
                  </div>
                  <div>
                    <div className="text-4xl font-black mb-1">
                      {itemsBought}
                    </div>
                    <div className="text-white/70 text-sm">
                      Items purchased
                    </div>
                  </div>
                </div>
                {/* Disclaimer */}
                <div className="flex items-start gap-2 mt-4 pt-4 border-t border-white/20">
                  <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-white/60" />
                  <p className="text-xs text-white/60">
                    Based on your purchase history. Click to view details.
                  </p>
                </div>
              </div>
            </div>
          </Link>
        )}

        {/* Main Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {statCards.map((stat) => (
            <Link
              key={stat.title}
              to={stat.link}
              className="bg-[var(--theme-card)] rounded-2xl border border-[var(--theme-border)] p-5 hover:shadow-lg transition-all hover:-translate-y-1 group"
            >
              <div className="flex items-start justify-between mb-3">
                <div className={`w-12 h-12 rounded-xl ${stat.color} flex items-center justify-center`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
                <ArrowRight className="w-5 h-5 text-[var(--theme-text-muted)] group-hover:text-[var(--theme-text-primary)] transition-colors" />
              </div>
              <div className="text-3xl font-black text-[var(--theme-text-primary)] mb-1">
                {stat.value}
              </div>
              <div className="text-sm font-medium text-[var(--theme-text-primary)]">{stat.title}</div>
              <div className="text-xs text-[var(--theme-text-muted)] mt-1">{stat.description}</div>
            </Link>
          ))}
        </div>

        {/* Transaction Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <Link
            to="/purchases"
            className="bg-[var(--theme-card)] rounded-2xl border border-[var(--theme-border)] p-5 hover:shadow-lg transition-all hover:-translate-y-1 group"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-[var(--theme-hover)] flex items-center justify-center">
                <ShoppingCart className="w-7 h-7 text-[var(--theme-text-primary)]" />
              </div>
              <div className="flex-1">
                <div className="text-2xl font-black text-[var(--theme-text-primary)]">{stats?.items_bought || 0}</div>
                <div className="text-sm font-medium text-[var(--theme-text-secondary)]">Items Bought</div>
                <div className="text-xs text-[#059669]">${(stats?.total_spent || 0).toLocaleString()} spent</div>
              </div>
              <ArrowRight className="w-5 h-5 text-[var(--theme-text-muted)] group-hover:text-[var(--theme-text-primary)] transition-colors" />
            </div>
          </Link>
          <div className="bg-[var(--theme-card)] rounded-2xl border border-[var(--theme-border)] p-5">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-[var(--theme-hover)] flex items-center justify-center">
                <DollarSign className="w-7 h-7 text-[var(--theme-text-primary)]" />
              </div>
              <div>
                <div className="text-2xl font-black text-[var(--theme-text-primary)]">{stats?.items_sold || 0}</div>
                <div className="text-sm font-medium text-[var(--theme-text-secondary)]">Items Sold</div>
                <div className="text-xs text-[#059669]">${(stats?.total_earned || 0).toLocaleString()} earned</div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div className="bg-gradient-to-br from-[#1E3A5F] to-[#2D4A6F] rounded-2xl p-6 text-white">
            <h3 className="text-lg font-bold mb-2">Looking for something?</h3>
            <p className="text-white/70 text-sm mb-4">
              Post what you want and let sellers find you
            </p>
            <Button 
              onClick={() => navigate('/create-wtb')}
              className="bg-white text-[#1E3A5F] hover:bg-white/90 rounded-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add a Want
            </Button>
          </div>
          
          <div className="bg-gradient-to-br from-[#C9956C] to-[#B8845B] rounded-2xl p-6 text-white">
            <h3 className="text-lg font-bold mb-2">Have items to sell?</h3>
            <p className="text-white/70 text-sm mb-4">
              List items with AI-powered photo scanning
            </p>
            <Button 
              onClick={() => navigate('/list/photos')}
              className="bg-white text-[#C9956C] hover:bg-white/90 rounded-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              List Items
            </Button>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Wants */}
          <div className="bg-[var(--theme-card)] rounded-2xl border border-[var(--theme-border)] overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--theme-border)]">
              <h3 className="font-bold text-[var(--theme-text-primary)]">Your Recent Wants</h3>
              <Link 
                to="/my-wants" 
                className="text-sm text-[#C9956C] hover:underline flex items-center gap-1"
              >
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="divide-y divide-[var(--theme-border)]">
              {recentWants.length > 0 ? (
                recentWants.map((want) => (
                  <div key={want.id} className="px-5 py-4 hover:bg-[var(--theme-hover)] transition-colors">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-[var(--theme-text-primary)]">{want.title}</h4>
                        <p className="text-sm text-[var(--theme-text-secondary)]">
                          Budget: ${want.min_price} - ${want.max_price}
                        </p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        want.status === 'active' 
                          ? 'bg-[#059669]/10 text-[#059669]' 
                          : 'bg-[var(--theme-text-muted)]/10 text-[var(--theme-text-muted)]'
                      }`}>
                        {want.status}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="px-5 py-8 text-center text-[var(--theme-text-muted)]">
                  <ShoppingBag className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p>No wants yet</p>
                  <Link to="/post-want" className="text-[#C9956C] text-sm hover:underline">
                    Post your first Want
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Recent Items */}
          <div className="bg-[var(--theme-card)] rounded-2xl border border-[var(--theme-border)] overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--theme-border)]">
              <h3 className="font-bold text-[var(--theme-text-primary)]">Your Recent Items</h3>
              <Link 
                to="/my-items" 
                className="text-sm text-[#C9956C] hover:underline flex items-center gap-1"
              >
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="divide-y divide-[var(--theme-border)]">
              {recentInventory.length > 0 ? (
                recentInventory.map((item) => {
                  // Handle image URL - can be string or object {ref, url, expires_at}
                  const imageUrl = item.images?.[0] 
                    ? (typeof item.images[0] === 'object' && item.images[0].url 
                        ? item.images[0].url 
                        : typeof item.images[0] === 'string'
                          ? (item.images[0].startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${item.images[0]}` : item.images[0])
                          : null)
                    : null;
                  
                  return (
                  <div key={item.id} className="px-5 py-4 hover:bg-[var(--theme-hover)] transition-colors">
                    <div className="flex items-center gap-4">
                      {imageUrl ? (
                        <img 
                          src={imageUrl} 
                          alt={item.title}
                          className="w-12 h-12 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-[var(--theme-hover)] flex items-center justify-center">
                          <Package className="w-6 h-6 text-[var(--theme-text-muted)]" />
                        </div>
                      )}
                      <div className="flex-1">
                        <h4 className="font-medium text-[var(--theme-text-primary)]">{item.title}</h4>
                        <p className="text-sm text-[#059669] font-semibold">
                          ${item.estimated_value?.toLocaleString()}
                        </p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        item.is_public 
                          ? 'bg-[#059669]/10 text-[#059669]' 
                          : 'bg-[var(--theme-text-muted)]/10 text-[var(--theme-text-muted)]'
                      }`}>
                        {item.is_public ? 'Public' : 'Passive'}
                      </span>
                    </div>
                  </div>
                  );
                })
              ) : (
                <div className="px-5 py-8 text-center text-[var(--theme-text-muted)]">
                  <Package className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p>No items yet</p>
                  <Link to="/list" className="text-[#C9956C] text-sm hover:underline">
                    List your first item
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Messages Alert */}
        {stats?.unread_messages > 0 && (
          <div className="mt-6 bg-[var(--theme-hover)] border border-[var(--theme-border)] rounded-2xl p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#1E3A5F] flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-medium text-[var(--theme-text-primary)]">
                    You have {stats.unread_messages} unread message{stats.unread_messages > 1 ? 's' : ''}
                  </p>
                  <p className="text-sm text-[var(--theme-text-secondary)]">Check your conversations</p>
                </div>
              </div>
              <Button 
                onClick={() => navigate('/matches')} 
                variant="outline"
                className="rounded-full border-[var(--theme-border)] text-[var(--theme-text-primary)]"
              >
                View Messages
              </Button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
